// Upgrades while no payment provider is wired (migration 20260823000300).
//
// The customer says what they want and how they intend to pay; it lands in a
// queue; an operator confirms the money arrived and applies the plan. Nothing
// here takes a payment — and nothing here decides a price either: the amount is
// quoted server-side from the price table, because a client-supplied number is
// a discount nobody approved.

import { supabase, callFunction } from "./supabase";
import { asAppError } from "./entitlements";
import type { BillingInterval } from "./types";

export interface PaymentMethod {
  code: string;
  name: string;
  description: string | null;
  instructions: string | null;
  regions: string[];
  sort_order: number;
}

export type RequestStatus =
  | "pending" | "awaiting_payment" | "paid" | "approved" | "rejected" | "cancelled";

export interface PlanChangeRequest {
  id: string;
  workspace_id: string;
  from_plan: string;
  to_plan: string;
  billing_interval: BillingInterval;
  region_code: string | null;
  currency: string | null;
  amount_minor: number | null;
  payment_method: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  note: string | null;
  status: RequestStatus;
  review_note: string | null;
  created_at: string;
  reviewed_at: string | null;
}

/** Still being worked on — the customer cannot open a second one. */
export const isOpen = (r: PlanChangeRequest): boolean =>
  r.status === "pending" || r.status === "awaiting_payment" || r.status === "paid";

export async function listPaymentMethods(region?: string | null): Promise<PaymentMethod[]> {
  const { data, error } = await supabase
    .from("payment_methods")
    .select("code, name, description, instructions, regions, sort_order")
    .eq("is_active", true)
    .order("sort_order");
  if (error) throw asAppError(error);

  const methods = data as PaymentMethod[];
  // An empty region list means "offered everywhere"; anything else is a
  // regional method (UPI in India, Zelle in the US) and is hidden elsewhere.
  return region
    ? methods.filter((m) => m.regions.length === 0 || m.regions.includes(region))
    : methods;
}

export async function listPlanChangeRequests(workspaceId: string): Promise<PlanChangeRequest[]> {
  const { data, error } = await supabase
    .from("plan_change_requests")
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("created_at", { ascending: false })
    .limit(20);
  if (error) throw asAppError(error);
  return data as PlanChangeRequest[];
}

export interface PlanRequestResult {
  request: PlanChangeRequest;
  email: { user: "sent" | "skipped" | "failed"; admin: "sent" | "skipped" | "failed" };
}

export async function requestPlanChange(args: {
  workspaceId: string;
  planCode: string;
  interval: BillingInterval;
  regionCode: string | null;
  paymentMethod: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  note?: string | null;
}): Promise<PlanRequestResult> {
  return callFunction<PlanRequestResult>("request-plan-change", {
    workspace_id: args.workspaceId,
    plan_code: args.planCode,
    billing_interval: args.interval,
    region_code: args.regionCode,
    payment_method: args.paymentMethod,
    contact_email: args.contactEmail ?? null,
    contact_phone: args.contactPhone ?? null,
    note: args.note ?? null,
  });
}

export async function cancelPlanChangeRequest(id: string): Promise<PlanChangeRequest> {
  const { data, error } = await supabase.rpc("cancel_plan_change_request", {
    p_request_id: id,
  });
  if (error) throw asAppError(error);
  return data as PlanChangeRequest;
}

/** What each status means to the customer, in their words rather than ours. */
export const STATUS_LABEL: Record<RequestStatus, string> = {
  pending: "Waiting for us to review",
  awaiting_payment: "Waiting for your payment",
  paid: "Payment received, applying your plan",
  approved: "Approved — your plan is active",
  rejected: "Not approved",
  cancelled: "Cancelled",
};
